import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasCapability } from '@prooffleet/contracts';
import { IconPlus } from '@tabler/icons-react';
import { ApiError, api, useSession } from '@prooffleet/client';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface ScheduleRow {
  _id: string;
  unitId: string | null;
  siteId: string | null;
  templateKey: string;
  cadence: string;
  timesOfDay: string[];
  daysOfWeek: number[] | null;
  graceMinutes: number;
  earlyMinutes: number;
  active: boolean;
}

interface Named {
  _id: string;
  name?: string;
  unitNumber?: string;
}

const DAYS = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '7', label: 'Sun' },
];

/**
 * Расписание ожидаемых осмотров.
 *
 * Слоты не материализуются заранее — они выводятся из расписания и
 * сопоставляются с фактическими осмотрами. Материализуется только пропуск,
 * когда окно закрылось: иначе правка расписания требовала бы чинить уже
 * созданные записи.
 *
 * Окно двустороннее. `earlyMinutes` существует потому, что водитель, вышедший
 * на полчаса раньше, иначе порождал бы фантомный пропуск.
 */
export function Schedules(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const canWrite = actor !== null && hasCapability(actor, 'threshold:write');
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<'site' | 'unit'>('site');
  const [siteId, setSiteId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [times, setTimes] = useState('06:00');
  const [days, setDays] = useState<string[]>(['1', '2', '3', '4', '5']);
  const [grace, setGrace] = useState<number | string>(180);
  const [early, setEarly] = useState<number | string>(120);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['schedules'],
    queryFn: () => api<{ schedules: ScheduleRow[] }>('/schedules'),
  });
  const { data: units } = useQuery({
    queryKey: ['units'],
    queryFn: () => api<{ units: Named[] }>('/units'),
  });
  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api<{ sites: Named[] }>('/sites'),
  });
  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ templates: { key: string; labels: Record<string, string> }[] }>('/templates'),
  });

  const schedules = data?.schedules ?? [];
  const unitName = new Map((units?.units ?? []).map((u) => [u._id, u.unitNumber ?? '—']));
  const siteName = new Map((sites?.sites ?? []).map((s) => [s._id, s.name ?? '—']));

  const create = useMutation({
    mutationFn: () =>
      api('/schedules', {
        method: 'POST',
        body: {
          unitId: target === 'unit' ? unitId : null,
          siteId: target === 'site' ? siteId : null,
          templateKey,
          cadence: 'daily',
          timesOfDay: times.split(',').map((t) => t.trim()).filter((t) => t !== ''),
          daysOfWeek: days.length === 7 ? null : days.map(Number),
          graceMinutes: Number(grace),
          earlyMinutes: Number(early),
          active: true,
        },
      }),
    onSuccess: async () => {
      setOpen(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not create the schedule');
    },
  });

  return (
    <Stack gap={0}>
      <PageHeader
        title="Schedules"
        count={isLoading ? undefined : schedules.length}
        hint="Expected inspections. A window that closes without one becomes a recorded miss, not an empty spot in the log."
        action={
          canWrite ? (
            <Button
              leftSection={<IconPlus size={15} stroke={1.8} />}
              onClick={() => {
                setError(null);
                setOpen(true);
              }}
            >
              Add schedule
            </Button>
          ) : undefined
        }
      />

      {isLoading && <TableSkeleton rows={2} cols={5} />}

      {!isLoading && schedules.length === 0 && (
        <EmptyState
          title="No schedules yet"
          hint="Without a schedule nothing is expected, so nothing can be missed. Add one to start the compliance log."
        />
      )}

      {!isLoading && schedules.length > 0 && (
        <Table.ScrollContainer minWidth={780}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="22%">Applies to</Table.Th>
                <Table.Th w="22%">Template</Table.Th>
                <Table.Th w="16%">Times</Table.Th>
                <Table.Th w="20%">Days</Table.Th>
                <Table.Th w="20%">Window</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {schedules.map((row) => (
                <Table.Tr key={row._id}>
                  <Table.Td>
                    <Text fz="sm" fw={560}>
                      {row.unitId !== null
                        ? (unitName.get(row.unitId) ?? 'unit')
                        : row.siteId !== null
                          ? (siteName.get(row.siteId) ?? 'site')
                          : 'whole fleet'}
                    </Text>
                    {!row.active && (
                      <Badge size="xs" color="gray" mt={2}>
                        inactive
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-mono">{row.templateKey}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-num" fz="sm">
                      {row.timesOfDay.join(', ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" c="gray.7">
                      {row.daysOfWeek === null
                        ? 'every day'
                        : row.daysOfWeek
                            .map((d) => DAYS.find((x) => x.value === String(d))?.label ?? d)
                            .join(' ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-num" fz="sm">
                      −{row.earlyMinutes} / +{row.graceMinutes} min
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal opened={open} onClose={() => setOpen(false)} title="New schedule">
        <Stack gap="md">
          <Select
            label="Applies to"
            data={[
              { value: 'site', label: 'Every unit at a site' },
              { value: 'unit', label: 'One unit' },
            ]}
            value={target}
            allowDeselect={false}
            onChange={(v) => setTarget(v === 'unit' ? 'unit' : 'site')}
          />

          {target === 'site' ? (
            <Select
              label="Site"
              data={(sites?.sites ?? []).map((s) => ({ value: s._id, label: s.name ?? '—' }))}
              value={siteId}
              onChange={setSiteId}
            />
          ) : (
            <Select
              label="Unit"
              searchable
              data={(units?.units ?? []).map((u) => ({ value: u._id, label: u.unitNumber ?? '—' }))}
              value={unitId}
              onChange={setUnitId}
            />
          )}

          <Select
            label="Template"
            data={(templates?.templates ?? []).map((t) => ({
              value: t.key,
              label: t.labels['en'] ?? t.key,
            }))}
            value={templateKey}
            onChange={setTemplateKey}
          />

          <TextInput
            label="Start times"
            description="Local wall time at the site. Two values for two shifts, comma separated."
            value={times}
            onChange={(e) => setTimes(e.currentTarget.value)}
            placeholder="06:00, 18:00"
          />

          <MultiSelect label="Days" data={DAYS} value={days} onChange={setDays} />

          <Group grow>
            <NumberInput
              label="Grace, minutes"
              description="Still on time after the window opens"
              value={grace}
              onChange={setGrace}
              min={15}
              max={720}
            />
            <NumberInput
              label="Early, minutes"
              description="Counted if the driver starts ahead of time"
              value={early}
              onChange={setEarly}
              min={0}
              max={720}
            />
          </Group>

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            loading={create.isPending}
            disabled={
              templateKey === null ||
              (target === 'site' && siteId === null) ||
              (target === 'unit' && unitId === null) ||
              days.length === 0
            }
            onClick={() => create.mutate()}
          >
            Create schedule
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
